import { stepMovement, type PlayerInput } from "../game/step.ts";
import { DEFAULT_GAME_SETTINGS, normalizeSettings, type GameSettings } from "../game/settings.ts";
import type { GameData, Participant } from "../game/model.ts";
import type { NetPlayer } from "./snapshot.ts";

/**
 * Client-side prediction for the local core.
 *
 * Everyone else is drawn from interpolated snapshots, deliberately ~100ms in the
 * past. Doing the same to your own core means your input only shows up a round trip
 * later, and the faster you move the further behind the drawn position sits — which
 * is why it reads as lurching most while boosting.
 *
 * So the local core is simulated here, immediately, with the *same* `stepMovement`
 * the server runs. Every snapshot re-anchors it: the server's position is taken as
 * truth and the inputs it has not acknowledged yet are replayed on top. The server
 * stays authoritative for everything — this only removes the visual delay.
 *
 * Prediction advances in **fixed steps at the server's tick rate**, and every step is
 * sent. Predicting per rendered frame while sending at a lower rate meant the replay
 * could not reproduce what the server actually did: a few pixels of error arrived
 * with every snapshot, and the correction yanked the core continuously — visible
 * only on your own core, and worse the faster you moved. One step, one sequence
 * number, one replay: the error is now zero unless the server genuinely disagrees.
 *
 * Between fixed steps the drawn position is extrapolated by velocity, so the core
 * still moves at screen rate rather than in 60Hz stair steps.
 */

type Frame = { seq: number; input: PlayerInput };

/** Bigger than any believable correction; past this the prediction is abandoned. */
const RESYNC_DISTANCE = 220;
/** Residual error is eased away over roughly this long rather than snapped. */
const SMOOTHING_PER_SECOND = 12;

export class LocalPrediction {
  private settings: GameSettings = DEFAULT_GAME_SETTINGS;
  private frames: Frame[] = [];
  private core: Participant | null = null;
  /** Difference between where we drew the core and where replay puts it. */
  private errorX = 0;
  private errorY = 0;
  private step = 1 / 60;
  /** Size of the last correction, in pixels. Zero means replay matched the server. */
  lastCorrection = 0;
  /** How many unacknowledged frames the last reconcile had to replay. */
  lastReplayDepth = 0;

  configure(balance: Partial<GameSettings>) {
    this.settings = normalizeSettings({ ...DEFAULT_GAME_SETTINGS, ...balance });
  }

  reset() {
    this.frames = [];
    this.core = null;
    this.errorX = 0;
    this.errorY = 0;
  }

  get active() {
    return this.core !== null;
  }

  /** One fixed simulation step, matching exactly what the server will do with it. */
  advance(game: GameData, input: PlayerInput, step: number, seq: number) {
    if (!this.core || step <= 0) return;
    this.step = step;
    stepMovement(game, this.core, input, step, this.settings);
    this.frames.push({ seq, input });
    // Anything the server has acknowledged is dropped on reconcile; this only
    // bounds the list if acknowledgements stop arriving entirely.
    if (this.frames.length > 240) this.frames.shift();

    const decay = Math.exp(-step * SMOOTHING_PER_SECOND);
    this.errorX *= decay;
    this.errorY *= decay;
  }

  /**
   * Re-anchors on the server's view of our core and replays whatever it had not
   * seen yet. The leftover difference is kept as an error that `advance` eases away,
   * so a correction slides in rather than snapping.
   */
  reconcile(game: GameData, authoritative: NetPlayer, ack: number) {
    const drawnX = this.core ? this.core.x + this.errorX : authoritative.x;
    const drawnY = this.core ? this.core.y + this.errorY : authoritative.y;

    this.core = this.core ?? blankCore(authoritative.i);
    this.core.x = authoritative.x;
    this.core.y = authoritative.y;
    this.core.vx = authoritative.vx;
    this.core.vy = authoritative.vy;
    this.core.orbs = authoritative.o;
    this.core.alive = authoritative.a === 1;
    this.core.boostRemaining = authoritative.bo === 1 ? 1 : 0;
    this.core.shield = authoritative.sh === 1 ? 1 : 0;

    this.frames = this.frames.filter((frame) => frame.seq > ack);
    this.lastReplayDepth = this.frames.length;
    for (const frame of this.frames) {
      stepMovement(game, this.core, frame.input, this.step, this.settings);
    }

    const errorX = drawnX - this.core.x;
    const errorY = drawnY - this.core.y;
    this.lastCorrection = Math.hypot(errorX, errorY);
    // Replay is exact now, so anything under a pixel is float noise. Carrying it
    // forward would keep the smoother permanently nudging the core.
    if (Math.hypot(errorX, errorY) < 1) {
      this.errorX = 0;
      this.errorY = 0;
      return;
    }
    if (Math.hypot(errorX, errorY) > RESYNC_DISTANCE) {
      // A respawn, a teleport, or a prediction that has lost the plot: show truth.
      this.errorX = 0;
      this.errorY = 0;
      return;
    }
    this.errorX = errorX;
    this.errorY = errorY;
  }

  /**
   * Overwrites the local core in a rendered world with the predicted position.
   * `partial` is the time already elapsed into the next fixed step; extrapolating
   * by velocity across it keeps the core moving at screen rate instead of stepping
   * at the simulation rate.
   */
  apply(players: NetPlayer[], id: string, partial = 0): NetPlayer[] {
    if (!this.core) return players;
    const core = this.core;
    const x = core.x + core.vx * partial + this.errorX;
    const y = core.y + core.vy * partial + this.errorY;
    return players.map((player) => (player.i === id
      ? { ...player, x, y, vx: core.vx, vy: core.vy }
      : player));
  }
}

function blankCore(id: string): Participant {
  return {
    id,
    kind: "human",
    name: "",
    color: "#ffffff",
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    orbs: 8,
    score: 0,
    alive: true,
    attackCooldown: 0,
    shield: 0,
    shieldCooldown: 0,
    boostRemaining: 0,
    respawn: 0,
    surgeCooldown: 0,
    spawnProtection: 0,
    targetX: 0,
    targetY: 0,
  };
}
